import { Navigate, useParams } from "react-router-dom";
import Loading from "~/components/Loading";
import { useFetcher } from "~/hooks/useFetcher";
import useUserPermission from "~/hooks/useUserPermission";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import { StatusMessage } from "~/pages/manifold-nextgen/components/StatusMessage";
import { resourceIcon } from "~/pages/manifold-nextgen/components/resourceIcon";
import {
  nextgenAdminConversationPath,
  nextgenConversationPath,
} from "~/pages/manifold-nextgen/paths";
import { VIEW_ALL_AI_CONVERSATIONS } from "~/router/permissions";
import { isApiError } from "~/utils/apiClient";

const { VITE_CONVERSATIONS_API } = import.meta.env;

// Only a project session's `resource_id` is a project id.
const PROJECT_APP_ID = "PROJECT_ASSISTANT";

/**
 * Resolves a bare session id to a page that can show it: the session in its
 * project for the owner, the admin view for a reviewer, and otherwise the
 * reason neither applies. Only project sessions have an owner page. Ownership is the server's call, read off the
 * owner-only conversation endpoint.
 */
export const ConversationRedirect = () => {
  const { conversationId = "" } = useParams<{ conversationId: string }>();
  const isReviewer = useUserPermission(VIEW_ALL_AI_CONVERSATIONS);
  const { data, error } = useFetcher<{ app: string; resource_id: string }>(
    `${VITE_CONVERSATIONS_API}/${conversationId}`,
    { shouldRetryOnError: false }
  );

  if (data?.app === PROJECT_APP_ID) {
    return <Navigate to={nextgenConversationPath(data.resource_id, conversationId)} replace />;
  }
  const isDenied = isApiError(error) && error.status === 403;
  if (isReviewer && (data || isDenied)) {
    return <Navigate to={nextgenAdminConversationPath(conversationId)} replace />;
  }
  if (data) {
    return (
      <StatusMessage
        title="This session can't be opened here"
        body="Only project sessions have a page in Manifold."
        icon={resourceIcon("conversation")}
      />
    );
  }
  if (error) {
    return <ResourceLoadError error={error} resourceLabel="conversation" />;
  }
  return <Loading />;
};
